import { createReducer, on } from '@ngrx/store';
import * as EquipmentActions from './equipment.actions';
import { EquipmentManufacturerDto } from '../../../../api/gse/models/equipment-manufacturer-dto';
import { EquipmentCategoryDto } from '../../../../api/gse/models/equipment-category-dto';
import { EquipmentModelDto } from '../../../../api/gse/models/equipment-model-dto';
import { Equipment } from '../../../../features/gse/inventory/equipment/pages/equipment-dashboard/equipment.types';

export interface EquipmentState {
  equipments: Equipment[];
  totalRecords: number;
  loading: boolean;
  submitting: boolean;
  createSuccess: boolean;
  uploading: boolean;
  uploadSuccess: boolean;
  currentRetry: number;
  error: string | null;
  uploadError: string | null;
  selectedEquipment: Equipment | null;
  // Dashboard state
  dashboardSummary: import('../../../../api/gse/models').EquipmentDashboardSummaryDto | null;
  dashboardSummaryLoading: boolean;
  dashboardSummaryError: string | null;
  statusChart: import('../../../../api/gse/models').EquipmentStatusChartDto | null;
  statusChartLoading: boolean;
  statusChartError: string | null;
  maintenanceWeeklyOverview:
    | import('../../../../api/gse/models').MaintenanceWeeklyOverviewDto[]
    | null;
  maintenanceWeeklyOverviewLoading: boolean;
  maintenanceWeeklyOverviewError: string | null;
  criticalAlerts: import('../../../../api/gse/models').MaintenanceAlertDtoResponse[] | null;
  criticalAlertsLoading: boolean;
  criticalAlertsError: string | null;
  upcomingMaintenances: import('../../../../api/gse/models').MaintenanceAlertDtoResponse[] | null;
  upcomingMaintenancesLoading: boolean;
  upcomingMaintenancesError: string | null;
  // Manufacturer Lookup state
  manufacturers: EquipmentManufacturerDto[];
  manufacturersLoading: boolean;
  manufacturersError: string | null;
  // Category Lookup state
  categories: EquipmentCategoryDto[];
  categoriesLoading: boolean;
  categoriesError: string | null;
  // Equipment Types list state (full paginated list of categories)
  equipmentTypes: EquipmentCategoryDto[];
  equipmentTypesTotalRecords: number;
  equipmentTypesLoading: boolean;
  equipmentTypesError: string | null;
  // Model Lookup state
  models: EquipmentModelDto[];
  modelsLoading: boolean;
  modelsError: string | null;
  // Equipment Logs state
  equipmentLogs: import('../../../../api/gse/models/equipment-log-dto').EquipmentLogDto[];
  equipmentLogsNext: string | undefined;
  equipmentLogsLoading: boolean;
  equipmentLogsError: string | null;
  // Equipment Maintenance Logs state
  equipmentMaintenanceLogs: import('../../../../api/gse/models/equipment-maintenance-log-dto').EquipmentMaintenanceLogDto[];
  equipmentMaintenanceLogsNext: number | undefined;
  equipmentMaintenanceLogsLoading: boolean;
  equipmentMaintenanceLogsError: string | null;
}

export const initialState: EquipmentState = {
  equipments: [],
  totalRecords: 0,
  loading: false,
  submitting: false,
  createSuccess: false,
  uploading: false,
  uploadSuccess: false,
  currentRetry: 0,
  error: null,
  uploadError: null,
  selectedEquipment: null,
  // Dashboard initial state
  dashboardSummary: null,
  dashboardSummaryLoading: false,
  dashboardSummaryError: null,
  statusChart: null,
  statusChartLoading: false,
  statusChartError: null,
  maintenanceWeeklyOverview: null,
  maintenanceWeeklyOverviewLoading: false,
  maintenanceWeeklyOverviewError: null,
  criticalAlerts: null,
  criticalAlertsLoading: false,
  criticalAlertsError: null,
  upcomingMaintenances: null,
  upcomingMaintenancesLoading: false,
  upcomingMaintenancesError: null,
  manufacturers: [],
  manufacturersLoading: false,
  manufacturersError: null,
  categories: [],
  categoriesLoading: false,
  categoriesError: null,
  equipmentTypes: [],
  equipmentTypesTotalRecords: 0,
  equipmentTypesLoading: false,
  equipmentTypesError: null,
  models: [],
  modelsLoading: false,
  modelsError: null,
  equipmentLogs: [],
  equipmentLogsNext: undefined,
  equipmentLogsLoading: false,
  equipmentLogsError: null,
  equipmentMaintenanceLogs: [],
  equipmentMaintenanceLogsNext: undefined,
  equipmentMaintenanceLogsLoading: false,
  equipmentMaintenanceLogsError: null,
};

export const equipmentReducer = createReducer(
  initialState,
  on(EquipmentActions.loadEquipments, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(EquipmentActions.loadEquipmentsSuccess, (state, { data, totalRecords }) => ({
    ...state,
    equipments: data,
    totalRecords,
    loading: false,
  })),
  on(EquipmentActions.loadEquipmentsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(EquipmentActions.loadEquipment, (state) => ({
    ...state,
    loading: true,
    error: null,
    selectedEquipment: null,
  })),
  on(EquipmentActions.loadEquipmentSuccess, (state, { equipment }) => ({
    ...state,
    selectedEquipment: equipment,
    loading: false,
  })),
  on(EquipmentActions.loadEquipmentFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(EquipmentActions.createEquipment, (state) => ({
    ...state,
    submitting: true,
    createSuccess: false,
    error: null,
  })),
  on(EquipmentActions.createEquipmentSuccess, (state) => ({
    ...state,
    submitting: false,
    createSuccess: true,
  })),
  on(EquipmentActions.createEquipmentFailure, (state, { error }) => ({
    ...state,
    submitting: false,
    error,
  })),
  on(EquipmentActions.updateEquipment, (state) => ({
    ...state,
    submitting: true,
    createSuccess: false,
    error: null,
  })),
  on(EquipmentActions.updateEquipmentSuccess, (state) => ({
    ...state,
    submitting: false,
    createSuccess: true,
  })),
  on(EquipmentActions.updateEquipmentFailure, (state, { error }) => ({
    ...state,
    submitting: false,
    error,
  })),
  on(EquipmentActions.uploadFiles, (state) => ({
    ...state,
    uploading: true,
    uploadSuccess: false,
    uploadError: null,
    currentRetry: 0,
  })),
  on(EquipmentActions.uploadFilesRetry, (state, { count }) => ({
    ...state,
    currentRetry: count,
  })),
  on(EquipmentActions.uploadFilesSuccess, (state) => ({
    ...state,
    uploading: false,
    uploadSuccess: true,
    currentRetry: 0,
  })),
  on(EquipmentActions.uploadFilesFailure, (state, { error }) => ({
    ...state,
    uploading: false,
    uploadError: error,
  })),
  on(EquipmentActions.resetUploadState, (state) => ({
    ...state,
    uploading: false,
    uploadSuccess: false,
    uploadError: null,
  })),
  // Dashboard reducers
  on(EquipmentActions.loadEquipmentDashboardSummary, (state) => ({
    ...state,
    dashboardSummaryLoading: true,
    dashboardSummaryError: null,
  })),
  on(EquipmentActions.loadEquipmentDashboardSummarySuccess, (state, { data }) => ({
    ...state,
    dashboardSummary: data,
    dashboardSummaryLoading: false,
  })),
  on(EquipmentActions.loadEquipmentDashboardSummaryFailure, (state, { error }) => ({
    ...state,
    dashboardSummaryLoading: false,
    dashboardSummaryError: error,
  })),
  on(EquipmentActions.loadEquipmentStatusChart, (state) => ({
    ...state,
    statusChartLoading: true,
    statusChartError: null,
  })),
  on(EquipmentActions.loadEquipmentStatusChartSuccess, (state, { data }) => ({
    ...state,
    statusChart: data,
    statusChartLoading: false,
  })),
  on(EquipmentActions.loadEquipmentStatusChartFailure, (state, { error }) => ({
    ...state,
    statusChartLoading: false,
    statusChartError: error,
  })),
  on(EquipmentActions.loadMaintenanceWeeklyOverview, (state) => ({
    ...state,
    maintenanceWeeklyOverviewLoading: true,
    maintenanceWeeklyOverviewError: null,
  })),
  on(EquipmentActions.loadMaintenanceWeeklyOverviewSuccess, (state, { data }) => ({
    ...state,
    maintenanceWeeklyOverview: data,
    maintenanceWeeklyOverviewLoading: false,
  })),
  on(EquipmentActions.loadMaintenanceWeeklyOverviewFailure, (state, { error }) => ({
    ...state,
    maintenanceWeeklyOverviewLoading: false,
    maintenanceWeeklyOverviewError: error,
  })),
  on(EquipmentActions.loadCriticalAlerts, (state) => ({
    ...state,
    criticalAlertsLoading: true,
    criticalAlertsError: null,
  })),
  on(EquipmentActions.loadCriticalAlertsSuccess, (state, { data }) => ({
    ...state,
    criticalAlerts: data,
    criticalAlertsLoading: false,
  })),
  on(EquipmentActions.loadCriticalAlertsFailure, (state, { error }) => ({
    ...state,
    criticalAlertsLoading: false,
    criticalAlertsError: error,
  })),
  on(EquipmentActions.loadUpcomingMaintenances, (state) => ({
    ...state,
    upcomingMaintenancesLoading: true,
    upcomingMaintenancesError: null,
  })),
  on(EquipmentActions.loadUpcomingMaintenancesSuccess, (state, { data }) => ({
    ...state,
    upcomingMaintenances: data,
    upcomingMaintenancesLoading: false,
  })),
  on(EquipmentActions.loadUpcomingMaintenancesFailure, (state, { error }) => ({
    ...state,
    upcomingMaintenancesLoading: false,
    upcomingMaintenancesError: error,
  })),
  // Manufacturer Lookup reducers
  on(EquipmentActions.loadManufacturerLookups, (state) => ({
    ...state,
    manufacturersLoading: true,
    manufacturersError: null,
  })),
  on(EquipmentActions.loadManufacturerLookupsSuccess, (state, { manufacturers }) => ({
    ...state,
    manufacturers,
    manufacturersLoading: false,
  })),
  on(EquipmentActions.loadManufacturerLookupsFailure, (state, { error }) => ({
    ...state,
    manufacturersLoading: false,
    manufacturersError: error,
  })),
  // Category Lookup reducers
  on(EquipmentActions.loadCategoryLookups, (state) => ({
    ...state,
    categoriesLoading: true,
    categoriesError: null,
  })),
  on(EquipmentActions.loadCategoryLookupsSuccess, (state, { categories }) => ({
    ...state,
    categories,
    categoriesLoading: false,
  })),
  on(EquipmentActions.loadCategoryLookupsFailure, (state, { error }) => ({
    ...state,
    categoriesLoading: false,
    categoriesError: error,
  })),
  // Equipment Types list reducers
  on(EquipmentActions.loadEquipmentTypes, (state) => ({
    ...state,
    equipmentTypesLoading: true,
    equipmentTypesError: null,
  })),
  on(EquipmentActions.loadEquipmentTypesSuccess, (state, { data, totalRecords }) => ({
    ...state,
    equipmentTypes: data,
    equipmentTypesTotalRecords: totalRecords,
    equipmentTypesLoading: false,
  })),
  on(EquipmentActions.loadEquipmentTypesFailure, (state, { error }) => ({
    ...state,
    equipmentTypesLoading: false,
    equipmentTypesError: error,
  })),
  // Model Lookup reducers
  on(EquipmentActions.loadModelLookups, (state) => ({
    ...state,
    modelsLoading: true,
    modelsError: null,
  })),
  on(EquipmentActions.loadModelLookupsSuccess, (state, { models }) => ({
    ...state,
    models,
    modelsLoading: false,
  })),
  on(EquipmentActions.loadModelLookupsFailure, (state, { error }) => ({
    ...state,
    modelsLoading: false,
    modelsError: error,
  })),
  // Equipment Logs reducers
  on(EquipmentActions.loadEquipmentLogs, (state) => ({
    ...state,
    equipmentLogsLoading: true,
    equipmentLogsError: null,
  })),
  on(EquipmentActions.loadEquipmentLogsSuccess, (state, { data, next }) => ({
    ...state,
    equipmentLogs: data,
    equipmentLogsNext: next,
    equipmentLogsLoading: false,
  })),
  on(EquipmentActions.loadEquipmentLogsFailure, (state, { error }) => ({
    ...state,
    equipmentLogsLoading: false,
    equipmentLogsError: error,
  })),
  // Load More Equipment Logs reducers (append to existing logs)
  on(EquipmentActions.loadMoreEquipmentLogs, (state) => ({
    ...state,
    equipmentLogsLoading: true,
    equipmentLogsError: null,
  })),
  on(EquipmentActions.loadMoreEquipmentLogsSuccess, (state, { data, next }) => ({
    ...state,
    equipmentLogs: [...state.equipmentLogs, ...data],
    equipmentLogsNext: next,
    equipmentLogsLoading: false,
  })),
  on(EquipmentActions.loadMoreEquipmentLogsFailure, (state, { error }) => ({
    ...state,
    equipmentLogsLoading: false,
    equipmentLogsError: error,
  })),
  // Equipment Maintenance Logs reducers
  on(EquipmentActions.loadEquipmentMaintenanceLogs, (state) => ({
    ...state,
    equipmentMaintenanceLogsLoading: true,
    equipmentMaintenanceLogsError: null,
  })),
  on(EquipmentActions.loadEquipmentMaintenanceLogsSuccess, (state, { data, next }) => ({
    ...state,
    equipmentMaintenanceLogs: data,
    equipmentMaintenanceLogsNext: next,
    equipmentMaintenanceLogsLoading: false,
  })),
  on(EquipmentActions.loadEquipmentMaintenanceLogsFailure, (state, { error }) => ({
    ...state,
    equipmentMaintenanceLogsLoading: false,
    equipmentMaintenanceLogsError: error,
  })),
  // Load More Equipment Maintenance Logs reducers (append to existing logs)
  on(EquipmentActions.loadMoreEquipmentMaintenanceLogs, (state) => ({
    ...state,
    equipmentMaintenanceLogsLoading: true,
    equipmentMaintenanceLogsError: null,
  })),
  on(EquipmentActions.loadMoreEquipmentMaintenanceLogsSuccess, (state, { data, next }) => ({
    ...state,
    equipmentMaintenanceLogs: [...state.equipmentMaintenanceLogs, ...data],
    equipmentMaintenanceLogsNext: next,
    equipmentMaintenanceLogsLoading: false,
  })),
  on(EquipmentActions.loadMoreEquipmentMaintenanceLogsFailure, (state, { error }) => ({
    ...state,
    equipmentMaintenanceLogsLoading: false,
    equipmentMaintenanceLogsError: error,
  }))
);
